import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { JobApplicationFormData } from "@/lib/validations/jobApplicationForm";

export const useJobApplication = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const submitApplication = async (
    jobId: string,
    jobTitle: string,
    data: JobApplicationFormData
  ) => {
    setIsSubmitting(true);
    let uploadedFilePath: string | null = null;

    try {
      // 1. Upload CV to storage
      const timestamp = Date.now();
      const sanitizedName = data.applicant_name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "-");
      const fileExt = data.cv_file.name.split(".").pop();
      const fileName = `${timestamp}-${sanitizedName}.${fileExt}`;
      const filePath = `${jobId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("job-applications")
        .upload(filePath, data.cv_file);

      if (uploadError) throw uploadError;
      uploadedFilePath = filePath;

      // 2. Save application to database
      const { data: application, error: dbError } = await supabase
        .from("job_applications")
        .insert({
          job_posting_id: jobId,
          applicant_name: data.applicant_name,
          applicant_email: data.applicant_email,
          applicant_phone: data.applicant_phone || null,
          linkedin_url: data.linkedin_url || null,
          cover_letter: data.cover_letter,
          cv_file_path: filePath,
          cv_file_name: data.cv_file.name,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // 3. Call edge function to send Slack notification
      const { error: functionError } = await supabase.functions.invoke(
        "send-job-application",
        {
          body: { applicationId: application.id },
        }
      );

      if (functionError) {
        console.error("Slack notification failed:", functionError);
        // Don't throw - application is saved successfully
      }

      toast({
        title: "Application submitted!",
        description: "We'll review your application and get back to you soon.",
      });

      // Navigate to thank you page with job title and name
      navigate(
        `/application-thank-you?job=${encodeURIComponent(jobTitle)}&name=${encodeURIComponent(data.applicant_name)}`
      );

      return { success: true };
    } catch (error: any) {
      console.error("Application submission error:", error);

      // Clean up uploaded file if database insert failed
      if (uploadedFilePath) {
        await supabase.storage
          .from("job-applications")
          .remove([uploadedFilePath]);
      }

      toast({
        title: "Submission failed",
        description:
          error.message || "Please try again or contact us directly.",
        variant: "destructive",
      });

      return { success: false, error: error.message };
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitApplication, isSubmitting };
};
